import { useState, useCallback } from 'react';
import type { VerticalLine } from '@/modules/shared/DataLayers/types';

export interface UseSideBandsManagementProps {
  verticalLines: VerticalLine[];
}

export interface UseSideBandsManagementReturn {
  // Side bands selections state (per chart)
  sideBandsSelectionsByChart: Record<string, any[]>;
  setSideBandsSelectionsByChart: React.Dispatch<React.SetStateAction<Record<string, any[]>>>;
  // Utility functions
  getSideBandsGroupCount: (sectionKey: string) => number;
  getAvailableSideBandsPrefixes: (sectionKey: string) => string[];
}

/**
 * Manages side bands (carrier) related state and utilities
 * Handles side bands selections per chart
 */
export function useSideBandsManagement({ verticalLines }: UseSideBandsManagementProps): UseSideBandsManagementReturn {
  // Side Bands selection state per chart
  const [sideBandsSelectionsByChart, setSideBandsSelectionsByChart] = useState<Record<string, any[]>>({});

  // Helper function to count CARRIER groups only (C, D, E prefixes)
  const getSideBandsGroupCount = useCallback(
    (sectionKey: string): number => {
      const sideBandsLinesInSection = verticalLines.filter((line: VerticalLine) => {
        if (!line.isHarmonic) return false;
        if (!(line.sectionKey === sectionKey || line.chartId === sectionKey)) return false;

        // Only count C, D, E prefixes
        const match = line.label?.match(/^-?([A-Z])\d+/);
        if (!match) return false;
        const prefix = match[1];
        return ['C', 'D', 'E'].includes(prefix);
      });

      // Get unique label prefixes (C, D, E)
      const uniquePrefixes = new Set<string>();
      sideBandsLinesInSection.forEach((line: VerticalLine) => {
        const match = line.label?.match(/^-?([A-Z])\d+/);
        if (match) uniquePrefixes.add(match[1]);
      });

      return uniquePrefixes.size;
    },
    [verticalLines]
  );

  // Helper function to get available side bands prefixes for a section
  // Returns array of unused prefixes from [C, D, E]
  // Considers both current prefixes AND originalPrefixes (which are "taken" by renamed groups)
  const getAvailableSideBandsPrefixes = useCallback(
    (sectionKey: string): string[] => {
      const allPrefixes = ['C', 'D', 'E'];
      const sideBandsLinesInSection = verticalLines.filter((line: VerticalLine) => {
        if (!line.isHarmonic) return false;
        if (!(line.sectionKey === sectionKey || line.chartId === sectionKey)) return false;

        const match = line.label?.match(/^-?([A-Z])\d+/);
        if (!match) return false;
        const prefix = match[1];
        return ['C', 'D', 'E'].includes(prefix) || ['C', 'D', 'E'].includes(line.originalPrefix || '');
      });

      // Get used prefixes (both current AND original)
      const usedPrefixes = new Set<string>();
      sideBandsLinesInSection.forEach((line: VerticalLine) => {
        // Add current prefix
        const match = line.label?.match(/^-?([A-Z])\d+/);
        if (match && ['C', 'D', 'E'].includes(match[1])) {
          usedPrefixes.add(match[1]);
        }
        // Add original prefix if exists (this means it was renamed, so original is "taken")
        if (line.originalPrefix && ['C', 'D', 'E'].includes(line.originalPrefix)) {
          usedPrefixes.add(line.originalPrefix);
        }
      });

      // Return available (unused) prefixes
      return allPrefixes.filter((p) => !usedPrefixes.has(p));
    },
    [verticalLines]
  );

  return {
    sideBandsSelectionsByChart,
    setSideBandsSelectionsByChart,
    getSideBandsGroupCount,
    getAvailableSideBandsPrefixes,
  };
}
